import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { SignalRow } from '@shared/flow/signal-view';
import type { EntitySignalRow } from '@shared/flow/entity-signals';
import { useFlowContactSignals } from '../contact/hooks';
import { getSignalRowActions, type SignalRowActions } from './rowActions';
import { useSignalsUiStore } from './useSignalsUiStore';
import { useGongCheckActivity } from './useRefreshSignals';
import type { ArchiveDraft } from './SignalActionArchiveForm';

/**
 * The Signal card's state (qa-batch5-plan.md D-A) — ONE hook that
 * `SignalDetail` calls BEFORE any branch, so it lives exactly as long as the
 * row expansion. `SignalDetail` itself is not remounted when it swaps its
 * loading-branch Fragment for `ContactDetail` (only its returned tree
 * changes; the card components under it ARE remounted), so the selection,
 * the dismissed set and the one open-form id survive that swap with no
 * module-scope field. Collapsing the row unmounts `SignalDetail`, so
 * re-expanding it starts fresh: the clicked row selected, the form CLOSED.
 *
 * - **Selector** over the contact's OPEN Signal Actions
 *   (`useFlowContactSignals`, filtered to `signal_action:` + To do/Snoozed).
 *   Default = the clicked row, which always stands in for its own id and is
 *   prepended when the contact list lacks it AND it is itself still open
 *   (limit truncation — never a row this card already dismissed).
 * - **Auto-advance and collapse** — every Snooze / Bring back / Archive
 *   success taken FROM THE CARD lands on `handleActed` (never the table's
 *   auto-expand-next `onActed`, which would replace this row mid-flow): the
 *   signal is marked dismissed and the selector moves to the next remaining
 *   open one; with none left `selected` is `null` and the card collapses in
 *   place.
 * - **Archive form** — closed unless the rep opens it: the card's Archive
 *   toggle (`setArchiveFormOpen`) or an archive intent (below). No
 *   state-driven auto-open (removed/paused used to open at mount — QA batch
 *   5 item 4). Switching the selector closes it (`selectId`, REVIEW-THESE
 *   R1), and closing UNMOUNTS the form — so the typed text is retained here,
 *   not in the form: `archiveDrafts` (plain `useState`, keyed by signal id)
 *   is written only by typing (`retainArchiveDraft`, the form's
 *   `onDraftChange`) and read only as the reopened form's seed. Cancel keeps
 *   it; `handleActed` drops the acted signal's entry; collapsing the row
 *   drops them all (fix-plan-batch5 D1, QA item 80). Only the DRAFT is
 *   mirrored — open-state stays the one `archiveOpenId`.
 * - **Archive intent** — a row's or Timeline line's Archive click sets
 *   `useSignalsUiStore.archiveIntent`. The effect acts only on an intent
 *   this card can answer for (one of its options: select it, open its form,
 *   scroll once per `seq`) and clears the intent only when it acted on it
 *   or the intent names this very row. An intent for another row is left in
 *   the store for that row's card — with row A expanded, a row-Archive on B
 *   sets the intent before B's card mounts, and A must not swallow it. The
 *   store, not a lifetime ref, holds the one-shot, so StrictMode's
 *   setup→cleanup→setup replays it idempotently (CLAUDE.md
 *   "StrictMode-Resilient Effects").
 */

/** The clicked table row (`SignalRow`) or a contact-signals row (`EntitySignalRow`, which carries no `flow_name`). */
export type CardSignal = SignalRow | EntitySignalRow;

const OPEN_ACTION_STATES = new Set(['To do', 'Snoozed']);

function isOpenCardSignal(signal: CardSignal, dismissedIds: ReadonlySet<string>): boolean {
  return OPEN_ACTION_STATES.has(signal.action_state) && !dismissedIds.has(signal.signal_id);
}

export interface SignalActionCardModel {
  row: SignalRow;
  now: Date;
  /** The contact's open Signal Actions, clicked row first-class (see the module doc). */
  options: CardSignal[];
  /** `null` = nothing open → the collapsed "No open signals" card. */
  selected: CardSignal | null;
  /** The selector's change handler — also closes the archive form (R1). */
  selectId: (id: string) => void;
  /** `getSignalRowActions(selected, now)`, or `null` with nothing selected. */
  actions: SignalRowActions | null;
  gong: { inFlight: boolean; lastCheckFailed: boolean };
  archiveFormOpen: boolean;
  setArchiveFormOpen: (open: boolean) => void;
  /** Every card-driven Snooze / Bring back / Archive success — also drops that signal's retained draft. */
  handleActed: (signalId: string) => void;
  /** Archive-form drafts retained per signal id across form remounts (see the module doc). */
  archiveDrafts: ReadonlyMap<string, ArchiveDraft>;
  /** The form's `onDraftChange` — the only writer of `archiveDrafts`. */
  retainArchiveDraft: (signalId: string, draft: ArchiveDraft) => void;
  /** The archive form's wrapper — the intent's one-shot scroll target. */
  formRef: RefObject<HTMLDivElement | null>;
  /** View as (U15): every write on the card is disabled and its archive form can never open. */
  readOnly?: boolean;
}

export function useSignalActionCard(row: SignalRow, now: Date, { readOnly = false }: { readOnly?: boolean } = {}): SignalActionCardModel {
  const contactSignals = useFlowContactSignals(row.contact_id);
  // PASSIVE read of the shared refresh query (qa-batch4 D-B) — never
  // `useRefreshSignals`, whose mount is what FIRES a refresh.
  const { inFlight, lastCheckFailed } = useGongCheckActivity();
  const loaded = contactSignals.data !== undefined;

  // Signals acted on FROM THIS CARD during this row expansion. Needed because
  // the mutation's `onSuccess` (which drives the advance) fires before the
  // list refetch it triggers lands, so `contactSignals.data` still shows the
  // OLD state for a beat; this set is the source of truth meanwhile.
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());

  const options = useMemo<CardSignal[]>(() => {
    if (!loaded) {
      // Before the contact fetch resolves, show the clicked row (if open) so
      // nothing flashes into "collapsed" while the fetch is in flight.
      return isOpenCardSignal(row, dismissedIds) ? [row] : [];
    }
    const openFromContact = (contactSignals.data?.rows ?? [])
      .filter(r => r.signal_id.startsWith('signal_action:'))
      .filter(r => isOpenCardSignal(r, dismissedIds));
    const idx = openFromContact.findIndex(r => r.signal_id === row.signal_id);
    if (idx !== -1) return openFromContact.map((r, i) => (i === idx ? row : r));
    // Not in the fetched open list: prepend the clicked row ONLY if it is
    // still genuinely open per its own data (limit truncation) — a row this
    // card just dismissed must NOT be revived.
    if (isOpenCardSignal(row, dismissedIds)) return [row, ...openFromContact];
    return openFromContact;
  }, [loaded, contactSignals.data, row, dismissedIds]);

  const [selectedId, setSelectedId] = useState(row.signal_id);
  const selected = options.find(o => o.signal_id === selectedId) ?? options[0] ?? null;

  // The ONE open-form id — replaces the old per-signal map + module-scope
  // `archiveOpenSignalId` (qa-batch5 D-A).
  const [archiveOpenId, setArchiveOpenId] = useState<string | null>(null);
  const [pendingScrollSeq, setPendingScrollSeq] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [archiveDrafts, setArchiveDrafts] = useState<ReadonlyMap<string, ArchiveDraft>>(() => new Map());
  const retainArchiveDraft = useCallback((signalId: string, draft: ArchiveDraft) => {
    setArchiveDrafts(prev => new Map(prev).set(signalId, draft));
  }, []);

  const selectId = useCallback((id: string) => {
    setSelectedId(id);
    setArchiveOpenId(null);
  }, []);

  /**
   * Marks the acted signal dismissed (so it can never be re-prepended) and,
   * from the options as they stood at the moment of the call, selects the
   * next remaining open one; with none left `options` recomputes to `[]` and
   * the card collapses in place — the row itself stays expanded.
   */
  const handleActed = useCallback(
    (actedId: string) => {
      setDismissedIds(prev => (prev.has(actedId) ? prev : new Set(prev).add(actedId)));
      setArchiveOpenId(prev => (prev === actedId ? null : prev));
      setArchiveDrafts(prev => {
        if (!prev.has(actedId)) return prev;
        const next = new Map(prev);
        next.delete(actedId);
        return next;
      });
      const idx = options.findIndex(o => o.signal_id === actedId);
      if (idx === -1) return;
      for (let step = 1; step < options.length; step++) {
        const candidate = options[(idx + step) % options.length];
        if (candidate.signal_id !== actedId) {
          setSelectedId(candidate.signal_id);
          return;
        }
      }
    },
    [options],
  );

  const archiveIntent = useSignalsUiStore(s => s.archiveIntent);
  const clearArchiveIntent = useSignalsUiStore(s => s.clearArchiveIntent);

  useEffect(() => {
    if (!archiveIntent) return;
    // Read-only (View as): never open the form for an intent; leave it for a writable card.
    if (readOnly) return;
    const owned = options.some(o => o.signal_id === archiveIntent.signalId);
    if (owned) {
      setSelectedId(archiveIntent.signalId);
      setArchiveOpenId(archiveIntent.signalId);
      setPendingScrollSeq(archiveIntent.seq);
    }
    // Clear only what this row can answer for — an intent naming another
    // row stays in the store for that row's card (see the module doc).
    if (owned || archiveIntent.signalId === row.signal_id) clearArchiveIntent();
  }, [archiveIntent, options, row.signal_id, clearArchiveIntent, readOnly]);

  useEffect(() => {
    if (pendingScrollSeq == null) return;
    formRef.current?.scrollIntoView({ block: 'nearest' });
    setPendingScrollSeq(null);
  }, [pendingScrollSeq]);

  const actions = selected ? getSignalRowActions(selected, now) : null;
  const archiveFormOpen = !readOnly && !!selected && !!actions?.showArchive && archiveOpenId === selected.signal_id;
  const selectedSignalId = selected?.signal_id ?? null;
  const setArchiveFormOpen = useCallback(
    (open: boolean) => setArchiveOpenId(open && !readOnly && selectedSignalId ? selectedSignalId : null),
    [selectedSignalId, readOnly],
  );

  return {
    row,
    now,
    options,
    selected,
    selectId,
    actions,
    gong: { inFlight, lastCheckFailed },
    archiveFormOpen,
    setArchiveFormOpen,
    handleActed,
    archiveDrafts,
    retainArchiveDraft,
    formRef,
    readOnly,
  };
}
